import { useEffect, useState, type ReactNode } from 'react';
import { FLOOR_ATLAS, findFurniture, furnitureFootprint } from '@campus/core';
import { Button, Icon } from '../ds/index.js';

/**
 * Illustrations du tutoriel des bureaux : petites scènes construites avec les
 * vrais éléments (sol et mobilier du jeu, boutons du design system) plutôt
 * que des captures figées — elles suivent le thème et l'interface.
 */

const CELL = 32; // 1 case = 32 px : pixel-art net, sans mise à l'échelle
const FLOOR_FRAME = 733; // parquet clair de l'atlas des sols

const DESK = 'lz_office_sheet_r7_28_3x2';
const CHAIR = 'lz_office_sheet_r1_8_1x2';
const SCREEN = 'lz_office_sheet_r14_8_1x2';
const PLANT = 'lz_office_sheet_r6_10_1x2';

/** Une case de l'atlas des sols, en image (le CSS ne sait pas répéter une partie d'une image). Calculée une fois. */
let floorTile: Promise<string> | null = null;
function loadFloorTile(): Promise<string> {
  floorTile ??= new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const t = FLOOR_ATLAS.tileSize;
      const canvas = document.createElement('canvas');
      canvas.width = t;
      canvas.height = t;
      canvas.getContext('2d')?.drawImage(img, (FLOOR_FRAME % FLOOR_ATLAS.columns) * t, Math.floor(FLOOR_FRAME / FLOOR_ATLAS.columns) * t, t, t, 0, 0, t, t);
      resolve(canvas.toDataURL());
    };
    img.onerror = () => { floorTile = null; reject(new Error('atlas des sols introuvable')); };
    img.src = FLOOR_ATLAS.url;
  });
  return floorTile;
}
let floorTileUrl: string | null = null;

function useFloorTile(): string | null {
  const [url, setUrl] = useState<string | null>(floorTileUrl);
  useEffect(() => {
    if (url) return;
    let alive = true;
    loadFloorTile().then((u) => { floorTileUrl = u; if (alive) setUrl(u); }).catch(() => { /* sol uni de repli */ });
    return () => { alive = false; };
  }, [url]);
  return url;
}

function Scene({ cols, rows, children }: { cols: number; rows: number; children: ReactNode }) {
  const floor = useFloorTile();
  return (
    <div
      aria-hidden
      className="pl-illu-scene"
      style={{
        width: cols * CELL, height: rows * CELL,
        backgroundImage: floor ? `url(${floor})` : undefined,
        backgroundSize: `${CELL}px ${CELL}px`,
      }}
    >
      {children}
    </div>
  );
}

/** Objet du catalogue ancré en bas à gauche sur la case (x, y), comme en jeu. */
function Sprite({ id, x, y, ghost }: { id: string; x: number; y: number; ghost?: boolean }) {
  const entry = findFurniture(id);
  if (!entry) return null;
  const f = furnitureFootprint(entry);
  return (
    <img
      src={entry.url}
      alt=""
      className={ghost ? 'pl-illu-ghost' : undefined}
      style={{ position: 'absolute', left: x * CELL, top: (y + 1 - f.height) * CELL, width: entry.dimensions.w, height: entry.dimensions.h, imageRendering: 'pixelated' }}
    />
  );
}

/** Rectangle de pose : vert (libre) ou rouge (occupé). */
function Spot({ x, y, w, h, ok, label }: { x: number; y: number; w: number; h: number; ok: boolean; label?: string }) {
  return (
    <div className={ok ? 'pl-illu-spot pl-illu-ok' : 'pl-illu-spot pl-illu-ko'} style={{ left: x * CELL, top: y * CELL, width: w * CELL, height: h * CELL }}>
      {label && <span className="pl-illu-spot-label">{label}</span>}
    </div>
  );
}

/** Emplacement libre, tel qu'il est tracé au sol en jeu : pointillés + « Libre ». */
function FreeSlot({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <div className="pl-illu-free" style={{ left: x * CELL, top: y * CELL, width: w * CELL, height: h * CELL }}>
      <span>Libre</span>
    </div>
  );
}

/** Pointeur de souris, avec un halo qui pulse là où cliquer. */
function Pointer({ x, y }: { x: number; y: number }) {
  return (
    <span className="pl-illu-pointer" style={{ left: x, top: y }}>
      <span className="pl-illu-pulse" />
      <svg width="16" height="20" viewBox="0 0 16 20"><path d="M1 1v15l4-4 3 7 3-1.4-3-6.6h6z" fill="#fff" stroke="#111" strokeWidth="1.4" strokeLinejoin="round" /></svg>
    </span>
  );
}

function NameTag({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return <span className="pl-illu-tag" style={{ left: x, top: y }}>{children}</span>;
}

/** Réplique non interactive d'une invite du jeu ; `target` = bouton à cliquer (halo). */
function PromptReplica({ label, actions, target }: { label: string; actions: string[]; target: string }) {
  return (
    <div aria-hidden className="pl-pop pl-illu-prompt">
      <Icon name="monitor" size={16} className="pl-menu-icon" />
      <span style={{ flex: 1, fontWeight: 500 }}>{label}</span>
      {actions.map((a) => (
        <span key={a} className={a === target ? 'pl-illu-target' : undefined}>
          {a === '?'
            ? <Button variant="ghost" size="sm" icon="help-circle" tabIndex={-1} aria-label="Comment ça marche ?" />
            : <Button variant={a === target ? 'primary' : 'ghost'} size="sm" tabIndex={-1}>{a}</Button>}
        </span>
      ))}
    </div>
  );
}

function Illustration({ children, caption }: { children: ReactNode; caption?: string }) {
  return (
    <figure className="pl-illu">
      {children}
      {caption && <figcaption className="pl-illu-caption">{caption}</figcaption>}
    </figure>
  );
}

/** Le bureau tel que les autres le voient : table, fauteuil, écran, plante, nom. */
function FurnishedDesk({ x }: { x: number }) {
  return (
    <>
      <Sprite id={CHAIR} x={x + 1} y={1} />
      <Sprite id={DESK} x={x} y={2} />
      {/* Centré devant le fauteuil, une demi-case plus bas : le pied de l'écran posé sur le plateau. */}
      <Sprite id={SCREEN} x={x + 1} y={1.5} />
    </>
  );
}

export function SceneWelcome() {
  return (
    <Illustration>
      <Scene cols={9} rows={4}>
        <FurnishedDesk x={2} />
        <Sprite id={PLANT} x={6} y={2} />
        <NameTag x={3.5 * CELL} y={4}>Bureau de Inès</NameTag>
      </Scene>
      <div aria-hidden className="pl-pop pl-illu-toast"><Icon name="bell" size={14} />Paul passe à ton bureau</div>
    </Illustration>
  );
}

export function ScenePlace() {
  return (
    <Illustration caption="Les emplacements libres sont tracés au sol. Place-toi sur l'un d'eux.">
      <PromptReplica label="Bureau libre" actions={['?', 'Prendre ce bureau']} target="Prendre ce bureau" />
      <Scene cols={10} rows={4}>
        <FreeSlot x={0} y={0} w={3} h={3} />
        <FurnishedDesk x={3} />
        <NameTag x={4.5 * CELL} y={4}>Bureau de Inès</NameTag>
        <FreeSlot x={6} y={0} w={3} h={3} />
        <Pointer x={7.4 * CELL} y={1.6 * CELL} />
      </Scene>
    </Illustration>
  );
}

export function SceneCustomize() {
  return (
    <Illustration caption="Choisis un objet dans le panneau, puis clique sur ton bureau.">
      <PromptReplica label="Ton bureau" actions={['?', 'Libérer', 'Personnaliser']} target="Personnaliser" />
      <Scene cols={9} rows={4}>
        <div className="pl-illu-grid" style={{ left: 3 * CELL, top: 0, width: 3 * CELL, height: 3 * CELL }} />
        <Sprite id={CHAIR} x={4} y={1} />
        <Sprite id={DESK} x={3} y={2} />
        <Sprite id={SCREEN} x={4} y={1.5} ghost />
        <Pointer x={4.6 * CELL} y={1.4 * CELL} />
      </Scene>
    </Illustration>
  );
}

export function SceneTips() {
  return (
    <Illustration caption="Bien : la rangée du fond reste libre pour ton fauteuil.">
      <Scene cols={5} rows={4}>
        <Sprite id={CHAIR} x={2} y={1} />
        <Sprite id={DESK} x={1} y={2} />
        <Spot x={1} y={0} w={3} h={3} ok />
      </Scene>
    </Illustration>
  );
}
